<template>
    <section class="wrap wrap-large py-12">
        <div
            class="max-w-[730px] at1024:max-w-[1120px] px-8 at1024:px-16 border w-full mx-auto border-slate-300 at1024:my-8"
        >
            <header class="text-center pt-8">
                <h1
                    class="text-[40px] at720:text-4xl my-[30px] at1024:text-[80px]"
                >
                    {{ $t("Account Web Access") }}
                </h1>
            </header>
            <p class="text-center text-[#97999B] text-[28px] at1024:text-[35px] font-medium leading-[1.2em] mb-[1.1em]">
                <span class="font-bold">{{ $t('IMPORTANT') }}:</span>
                {{ $t('Your company must already have an account with us to sign up for web access.') }}
            </p>
            <form
                class="flex flex-col gap-3"
                @invalid="invalidate"
                @submit.prevent="submitForm"
                :class="{ errors: errors }"
            >
                <div class="flex flex-col at768:flex-row gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="First Name"
                            :animated="true"
                            :contrast="true"
                            v-model="form.first_name"
                            pattern="[A-Za-z\s]{1,30}"
                            required
                            @invalid="invalidate"
                        />
                    </div>
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="Last Name"
                            :animated="true"
                            :contrast="true"
                            v-model="form.last_name"
                            required
                            pattern="[A-Za-z\s]{1,30}"
                            @invalid="invalidate"
                        />
                    </div>
                </div>
                <SeclockInput
                    type="text"
                    label="Title"
                    :animated="true"
                    :contrast="true"
                    v-model="form.title"
                    pattern="[A-Za-z\s]{1,50}"
                    required
                    @invalid="invalidate"
                />
                <div class="at768:flex-row flex flex-col gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="tel"
                            label="Phone"
                            :animated="true"
                            :contrast="true"
                            v-model="form.phone"
                            pattern="(1)?(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{4}"
                            required
                            @invalid="invalidate"
                        />
                    </div>
                    <div class="at768:w-1/2 at768:flex-row flex-col flex gap-3">
                        <div class="at768:w-1/2">
                            <SeclockInput
                                type="text"
                                label="Extension"
                                :animated="true"
                                :contrast="true"
                                v-model="form.extension"
                            />
                        </div>
                        <div class="at768:w-1/2">
                            <SeclockInput
                                type="select"
                                label="Phone Type"
                                :animated="true"
                                :contrast="true"
                                v-model="form.phone_type"
                                required
                                @invalid="invalidate"
                            >
                                <option
                                    v-for="phoneType in config.phoneTypes"
                                    :value="phoneType"
                                >
                                    {{ phoneType }}
                                </option>
                            </SeclockInput>
                        </div>
                    </div>
                </div>

                <div class="at768:flex-row flex-col flex gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="Company"
                            :animated="true"
                            :contrast="true"
                            v-model="form.company_name"
                            maxlength="35"
                            minlength="1"
                            required
                            @invalid="invalidate"
                        />
                    </div>
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="SECLOCK Account/Customer #"
                            :animated="true"
                            :contrast="true"
                            v-model="form.account_number"
                            error="Don't know your account #? Email <a href='mailTo:accountsignup@seclock.com'>AccountSignup@SECLOCK.com</a>."
                            required
                            @invalid="invalidate"
                        />
                    </div>
                </div>

                <h5
                    class="text-center text-[#63666A] text-[20px] my-6 at1024:text-[23px]"
                >
                    {{ $t("Set up your login information") }}
                </h5>

                <SeclockInput
                    type="email"
                    label="Email"
                    :animated="true"
                    :contrast="true"
                    v-model="form.email"
                    required
                    @invalid="invalidate"
                />
                <PasswordRequirements :password="form.password" />
                <div class="at768:flex-row flex-col flex gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="password"
                            label="Password"
                            :animated="true"
                            :contrast="true"
                            v-model="form.password"
                            required
                            @invalid="invalidate"
                            describedby="password-requirements"
                        />
                    </div>
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="password"
                            label="Confirm Password"
                            :animated="true"
                            :contrast="true"
                            v-model="form.password_confirmation"
                            required
                            @invalid="invalidate"
                        />
                    </div>
                </div>
                <button
                    :disabled="!isFormValid"
                    type="submit"
                    class="btn-white-on-red mb-12 flex justify-center"
                >
                    {{ $t("SUBMIT") }}
                </button>
                <RecaptchaDisclaimer class="mb-16 text-center text-gray text-sm"/>
            </form>
        </div>
    </section>
    <Meta title="Account Web Access" />
</template>

<script setup>
import {Meta, PasswordRequirements, RecaptchaDisclaimer, SeclockInput} from "@/Components";
import { computed, ref } from "vue";
import config from "../../config";

const props = defineProps(["form"]);

const emit = defineEmits(["submitForm"]);

const errors = ref(false);

function invalidate() {
    errors.value = true;
}

const submitForm = () => {
    if(isFormValid.value) {
        emit("submitForm");
    }
};

const isFormValid = computed(() => {
    // Check first and last names for correct character use and length
    const namePattern = /^[A-Za-z\s]{1,30}$/;
    if (!namePattern.test(props.form.first_name) || !namePattern.test(props.form.last_name)) return false;

    // Check title for correct character use and length
    const titlePattern = /^[A-Za-z\s]{1,50}$/;
    if (!titlePattern.test(props.form.title)) return false;

    // Check phone number for correct format
    const phonePattern = /(1)?(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{4}/;
    if (!phonePattern.test(props.form.phone)) return false;

    // Check if the phone type is selected (assuming 'config.phoneTypes' contains valid types)
    if (!config.phoneTypes.includes(props.form.phone_type)) return false;

    // Check company name for valid characters and length
    // const companyNamePattern = /^[A-Za-z0-9\s_&+-]{1,35}$/;
    // if (!companyNamePattern.test(props.form.company_name)) return false;
    if (!props.form.company_name) return false;

    if (props.form.account_number?.length < 5) return false;

    // Validate email using a basic pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(props.form.email)) return false;

    // Check password for requirements and match with confirmation
    if (!props.form.password || props.form.password !== props.form.password_confirmation) return false;

    // All validations pass
    return true;
});
</script>
